import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Heading,
  useToast,
  Container,
  Flex,
  Divider,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Select,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  getDynamicForecast,
  GetInventoryListing,
} from "../service/ForecastService";
import { getTableColumns, TableColumnItem } from "../service/tableService";
import { FieldValue } from "../constants/Types";

interface AnalysisForecastingProps {
  onClose: () => void;
  onOpenAdvanced: () => void;
}

const AnalysisForecasting: React.FC<AnalysisForecastingProps> = ({
  onClose,
  onOpenAdvanced,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const [horizon, setHorizon] = useState<number>(7);
  const [forecastData, setForecastData] = useState<any[]>([]);
  const [modifiedData, setModifiedData] = useState<any[]>([]);
  const [tableColumns, setTableColumns] = useState<TableColumnItem[]>([]);
  const [itemList, setItemList] = useState<FieldValue[]>([]);
  const [itemId, setItemId] = useState<string>("");
  const [targetColumn, setTargetColumn] = useState<string>("");
  useEffect(() => {
    if (itemId && targetColumn) {
      fetchForecast();
    }
  }, [itemId, targetColumn]);

  useEffect(() => {
    async function loadDropdownData() {
      try {
        // Fetch both dropdown datasets
        const [columns, items] = await Promise.all([
          getTableColumns("InventoryRecords"),
          GetInventoryListing(),
        ]);

        // Filter only numeric/forecastable columns by name
        const forecastable = columns.filter((col) =>
          ["QuantityAvailable", "QuantitySold", "QuantityRestocked"].includes(
            col.value
          )
        );

        setTableColumns(forecastable);
        setItemList(items);

        // Set defaults only once on load
        if (items.length > 0) setItemId(items[0].value);
        if (forecastable.length > 0) setTargetColumn(forecastable[0].value);
      } catch (error) {
        toast({ title: "Failed to load dropdown data", status: "error" });
      }
    }

    loadDropdownData();
  }, []);

  const fetchForecast = async () => {
    try {
      const result = await getDynamicForecast(itemId, targetColumn, horizon);
      setForecastData(result.forecast);
      setModifiedData(result.modifiedTrainingData);
    } catch (error) {
      toast({ title: "Failed to fetch forecast", status: "error" });
    }
  };

  const downloadPDF = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF();
    pdf.addImage(imgData, "PNG", 10, 10, 180, 90);
    pdf.save("analysis-forecast.pdf");
  };
  const downloadJSON = (data: any, filename: string) => {
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  };

  const downloadCSV = (data: any[], filename: string) => {
    const csv = [
      Object.keys(data[0]).join(","), // headers
      ...data.map((row) =>
        Object.values(row)
          .map((val) => `"${val}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  };
  return (
    <Container maxW="4xl" py={6}>
      <Heading size="lg" mb={4}>
        📊 Analysis Forecasting
      </Heading>

      <Flex gap={3} mb={4}>
        <Select value={itemId} onChange={(e) => setItemId(e.target.value)}>
          {itemList.map((item) => (
            <option key={item.value} value={item.value}>
              {item.filed}
            </option>
          ))}
        </Select>

        <Select
          value={targetColumn}
          onChange={(e) => setTargetColumn(e.target.value)}
        >
          {tableColumns
            .filter((col) =>
              [
                "QuantityAvailable",
                "QuantitySold",
                "QuantityRestocked",
              ].includes(col.value)
            )
            .map((col) => (
              <option key={col.value} value={col.value}>
                {col.label}
              </option>
            ))}
        </Select>

        <input
          type="number"
          min={1}
          value={horizon}
          onChange={(e) => setHorizon(Number(e.target.value))}
          style={{
            width: "80px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "4px",
          }}
          placeholder="Days"
        />
      </Flex>

      <Divider mb={4} />

      {forecastData.length > 0 && (
        <Box
          ref={chartRef}
          p={4}
          borderWidth={1}
          borderRadius="lg"
          bg="white"
          shadow="md"
          mb={6}
        >
          <Text fontWeight="bold" mb={2}>
            📈 Forecast for {targetColumn} (Next {forecastData.length} Days)
          </Text>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) =>
                  new Date(date).toLocaleDateString("en-GB")
                }
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="predictedValue"
                stroke="#2B6CB0"
                name="Forecasted Value"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {modifiedData.length > 0 && (
        <Box p={4} borderWidth={1} borderRadius="md" bg="gray.50">
          <Text fontWeight="bold" mb={2}>
            🛠 Modified Training Data
          </Text>
          <Flex gap={3}>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => downloadJSON(modifiedData, "modified-data.json")}
            >
              📥 Download JSON
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              onClick={() => downloadCSV(modifiedData, "modified-data.csv")}
            >
              📥 Download CSV
            </Button>
          </Flex>
        </Box>
      )}

      <Box mt={4}>
        <Button
          size="sm"
          variant="link"
          colorScheme="blue"
          onClick={onOpenAdvanced}
        >
          ← Back to Forecast Selection
        </Button>
      </Box>
    </Container>
  );
};

export default AnalysisForecasting;
